// SLA Types based on backend module
// Reference: /docs/sla/README.md

// Enums
export type SLATier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'custom'
export type SLASource = 'template' | 'manual' | 'contract' | 'custom'
export type SLAType = 'template' | 'custom' | 'contract-based' // Backend naming
export type SLAStatus = 'active' | 'inactive' | 'suspended'
export type DowntimeType = 'unplanned' | 'planned' | 'maintenance' | 'emergency'
export type DowntimeCause =
    | 'provider'
    | 'customer'
    | 'third_party'
    | 'force_majeure'
    | 'scheduled'
export type DowntimeStatus = 'open' | 'resolved' | 'acknowledged'

// SLA Rule - defines response/resolution times per ticket type
export interface SLARule {
    ticket_type_group: string
    ticket_type_key?: string | null
    response_time_minutes: number
    resolution_time_minutes: number
    priority?: string
    business_hours_only?: boolean
}

// Custom Term - contract-specific terms
export interface CustomTerm {
    key: string
    value: string
    category?: string
}

// Escalation Condition - matches backend EscalationCondition struct
export interface EscalationCondition {
    ticket_type_group?: string
    priority?: string[]
    no_response_after_minutes: number
}

// Escalation Rule - defines escalation conditions and targets
export interface EscalationRule {
    condition: EscalationCondition
    escalate_to: {
        employee_ids?: number[]
        roles?: string[]
    }
    notification_channels: string[]
    escalation_level: number
}

// Business Hours Config - matches backend BusinessHoursConfig struct
export interface BusinessHoursConfig {
    id?: string
    timezone: string // time_zone in backend
    work_days: number[] // 0=Sunday, 1-6=Mon-Sat
    work_hours: {
        start: string // start_time in backend, e.g. "09:00"
        end: string // end_time in backend, e.g. "17:00"
    }
    holidays?: string[] // Dates to exclude (ISO format)
    include_weekends?: boolean // Calculate across 24/7 if true
}

// SLA Template - matches backend SLATemplate struct
export interface SLATemplate {
    sla_template_id: number
    template_name: string
    template_tier: SLATier // tier in backend
    template_description?: string
    uptime_guarantee: number // uptime_percentage in backend, e.g. 99.9
    downtime_allowance_minutes: number
    default_response_time: number // minutes
    default_resolution_time: number // minutes
    business_hours_only: boolean
    business_hours_config?: BusinessHoursConfig
    holiday_dates?: string[] // YYYY-MM-DD list stored as JSONB
    sla_rules: SLARule[] // response_time_sla_rules + resolution_time_sla_rules combined
    escalation_rules: EscalationRule[]
    custom_terms?: CustomTerm[]
    is_active: boolean
    created_at: string
    updated_at: string
}

// Create/Update SLA Template payload
export interface SLATemplatePayload {
    template_name: string
    template_tier: SLATier
    template_description?: string
    uptime_guarantee?: number
    downtime_allowance_minutes?: number
    default_response_time?: number
    default_resolution_time?: number
    business_hours_only?: boolean
    business_hours_config?: BusinessHoursConfig
    holiday_dates?: string[] // YYYY-MM-DD list; BE accepts as json.RawMessage array
    sla_rules?: SLARule[]
    escalation_rules?: EscalationRule[]
    custom_terms?: CustomTerm[]
    is_active?: boolean
}

// Service SLA - per-service SLA configuration
// Matches backend ServiceSLA struct
export interface ServiceSLA {
    service_sla_id: number
    service_id: number
    customer_id?: number // Reference to customer
    sla_template_id?: number // template_id in backend
    sla_template?: SLATemplate
    sla_source: SLASource // sla_type in backend: template|custom|contract-based
    effective_from: string // active_from in backend
    effective_to?: string // active_until in backend (null = indefinite)
    uptime_guarantee?: number // uptime_percentage in backend
    downtime_allowance_minutes?: number
    business_hours_only?: boolean
    business_hours_config?: BusinessHoursConfig
    sla_rules: SLARule[]
    escalation_rules: EscalationRule[]
    custom_terms?: CustomTerm[]
    contract_id?: number
    status?: SLAStatus // active|inactive|suspended
    sla_status?: SLAStatus // Backend also returns this
    is_active: boolean
    created_at: string
    updated_at: string
    // Computed effective values from backend
    effective_uptime_guarantee?: number
    effective_downtime_allowance?: number
    effective_business_hours_only?: boolean
    effective_business_hours_config?: BusinessHoursConfig
    effective_sla_rules?: SLARule[]
    effective_escalation_rules?: EscalationRule[]
    // Template info if from template
    template_name?: string
    template_tier?: { sla_tier: SLATier | ''; valid: boolean }
    template_default_response_time?: number
    template_default_resolution_time?: number
    // Joined fields from `GetAllServiceSLAs` SQL (registry view)
    customer_id?: number
    customer_name?: string
    company_id?: number
    company_name?: string
    service_name?: string
    product_group_name?: string
    service_status?: string
    service_start_date?: string | null
    service_stopped_date?: string | null
    cs_followers?: string[]
    sale_followers?: string[]
}

// Create Service SLA payload
export interface ServiceSLAPayload {
    sla_source?: SLASource
    sla_template_id?: number
    effective_from?: string
    effective_to?: string
    uptime_guarantee?: number
    downtime_allowance_minutes?: number
    business_hours_only?: boolean
    business_hours_config?: BusinessHoursConfig
    sla_rules?: SLARule[]
    escalation_rules?: EscalationRule[]
    custom_terms?: CustomTerm[]
    contract_id?: number
    status?: SLAStatus
    is_active?: boolean
}

// Apply Template payload
export interface ApplyTemplatePayload {
    sla_template_id: number
}

// Downtime Log - matches backend SLADowntimeLog struct
export interface SLADowntimeLog {
    downtime_log_id: number
    service_sla_id?: number
    service_id: number
    downtime_type: DowntimeType
    downtime_cause?: DowntimeCause // provider|customer|third_party|force_majeure|scheduled
    downtime_start: string
    downtime_end?: string // null if ongoing
    downtime_minutes?: number // duration_minutes in backend
    ticket_id?: number
    downtime_description?: string // reason in backend
    impact?: string // Services affected
    root_cause?: string
    counts_against_sla: boolean
    is_excluded?: boolean // True if counted as maintenance window
    excluded_reason?: string
    status?: DowntimeStatus // open|resolved|acknowledged
    sla_period: string // first day of month
    created_by?: number
    created_at: string
    updated_at: string
}

// Create Downtime Log payload
export interface DowntimeLogPayload {
    downtime_type?: DowntimeType
    downtime_cause?: DowntimeCause
    downtime_start: string
    downtime_end?: string
    ticket_id?: number
    downtime_description?: string
    impact?: string
    root_cause?: string
    counts_against_sla?: boolean
    is_excluded?: boolean
    excluded_reason?: string
}

// End Downtime payload
export interface EndDowntimePayload {
    downtime_end: string
}

// Uptime Stats response
export interface UptimeStats {
    service_id: number
    target_uptime: number
    allowed_downtime: number
    total_downtime_minutes: number
    incident_count: number
    actual_uptime: number
    sla_met: boolean
}

// Monthly Summary (with cause breakdown)
export interface MonthlySummary {
    month: string
    sla_period?: string // first day of month
    target_uptime: number
    actual_uptime: number
    sla_downtime_minutes?: number // Only counts provider/third_party
    total_downtime_minutes: number
    incident_count: number
    unplanned_count?: number
    planned_count?: number
    maintenance_count?: number
    emergency_count?: number
    // Cause-based breakdown
    provider_downtime_mins?: number
    customer_downtime_mins?: number
    third_party_downtime_mins?: number
    force_majeure_downtime_mins?: number
    scheduled_downtime_mins?: number
    sla_met: boolean
}

// SLA Tier reference data - aligned with backend tier levels
// Reference: /docs/sla/README.md
export const SLA_TIER_CONFIG: Record<
    Exclude<SLATier, 'custom'>,
    {
        uptime: number
        downtimePerMonth: string
        responseTime: string
        responseTimeMinutes: number
        resolutionTime: string
        resolutionTimeMinutes: number
        color: string
    }
> = {
    bronze: {
        uptime: 99.0,
        downtimePerMonth: '~7.3 giờ',
        responseTime: '8 giờ',
        responseTimeMinutes: 480,
        resolutionTime: '24 giờ',
        resolutionTimeMinutes: 1440,
        color: '#CD7F32',
    },
    silver: {
        uptime: 99.5,
        downtimePerMonth: '~3.6 giờ',
        responseTime: '4 giờ',
        responseTimeMinutes: 240,
        resolutionTime: '12 giờ',
        resolutionTimeMinutes: 720,
        color: '#C0C0C0',
    },
    gold: {
        uptime: 99.9,
        downtimePerMonth: '~43 phút',
        responseTime: '2 giờ',
        responseTimeMinutes: 120,
        resolutionTime: '8 giờ',
        resolutionTimeMinutes: 480,
        color: '#FFD700',
    },
    platinum: {
        uptime: 99.99,
        downtimePerMonth: '~4 phút',
        responseTime: '1 giờ',
        responseTimeMinutes: 60,
        resolutionTime: '4 giờ',
        resolutionTimeMinutes: 240,
        color: '#E5E4E2',
    },
}

// Downtime type labels
export const DOWNTIME_TYPE_LABELS: Record<DowntimeType, string> = {
    unplanned: 'Ngoài kế hoạch',
    planned: 'Có kế hoạch',
    maintenance: 'Bảo trì',
    emergency: 'Khẩn cấp',
}

// SLA Source labels
export const SLA_SOURCE_LABELS: Record<SLASource, string> = {
    template: 'Từ template',
    manual: 'Cấu hình thủ công',
    contract: 'Từ hợp đồng',
    custom: 'Tùy chỉnh',
}

// SLA Status labels
export const SLA_STATUS_LABELS: Record<SLAStatus, string> = {
    active: 'Đang hoạt động',
    inactive: 'Không hoạt động',
    suspended: 'Tạm dừng',
}

// Downtime Status labels
export const DOWNTIME_STATUS_LABELS: Record<DowntimeStatus, string> = {
    open: 'Đang mở',
    resolved: 'Đã giải quyết',
    acknowledged: 'Đã xác nhận',
}

// Downtime Cause labels
export const DOWNTIME_CAUSE_LABELS: Record<DowntimeCause, string> = {
    provider: 'Lỗi nội bộ (bên mình)',
    customer: 'Lỗi khách hàng',
    third_party: 'Bên thứ ba',
    force_majeure: 'Bất khả kháng',
    scheduled: 'Bảo trì định kỳ',
}

// Downtime cause determines if it counts against SLA by default
export const DOWNTIME_CAUSE_COUNTS_SLA: Record<DowntimeCause, boolean> = {
    provider: true, // Our fault - counts
    customer: false, // Customer's fault - excluded
    third_party: true, // External provider - counts (configurable)
    force_majeure: false, // Natural disaster - excluded
    scheduled: false, // Pre-announced maintenance - excluded
}

// ============== Ticket SLA Compliance Types ==============

// Summary of at-risk and approaching tickets
export interface TicketSLASummary {
    at_risk_count: number
    approaching_count: number
    total_warning: number
}

// Ticket that breached SLA (past 100%)
export interface TicketAtRisk {
    ticket_id: number
    service_id: number
    service_name?: string
    customer_id?: number
    customer_name: string
    ticket_type_group?: string
    ticket_type_key?: string
    elapsed_minutes: number
    sla_threshold: number // response or resolution time limit
    breach_percent: number // >100% means breached
    breached_at?: string
    created_at?: string
}

// Ticket approaching deadline (80-100% elapsed)
export interface TicketApproaching {
    ticket_id: number
    service_id: number
    service_name?: string
    customer_id?: number
    customer_name: string
    ticket_type_group?: string
    ticket_type_key?: string
    elapsed_minutes: number
    sla_threshold: number
    progress_percent: number // 80-100%
    deadline_at?: string
    created_at?: string
}

// ============== Compliance Dashboard Types ==============

// Aggregate compliance metrics (response/resolution time)
export interface ComplianceOverview {
    total_services: number
    total_response_met: number
    total_response_breached: number
    total_resolution_met: number
    total_resolution_breached: number
    total_at_risk: number
    avg_compliance_pct: number
    services_above_95: number
    services_below_95: number
}

// Compliance aggregated by customer
export interface CustomerCompliance {
    customer_id: number
    customer_name: string
    service_count: number
    response_met: number
    response_breached: number
    resolution_met: number
    resolution_breached: number
    avg_compliance_pct: number
}

// Service with at-risk tickets
export interface ServiceAtRisk {
    snapshot_id: number
    service_id: number
    service_name: string
    customer_id?: number
    customer_name: string
    pending_at_risk_count: number
}

// ============== SLA History & Changes Types ==============

// SLA change log entry
export interface SLAChangeLog {
    change_id: number
    service_id: number
    service_name?: string
    customer_id?: number
    customer_name?: string
    change_type: 'created' | 'updated' | 'deleted' | 'template_applied'
    old_value?: Record<string, unknown>
    new_value?: Record<string, unknown>
    changed_by?: number
    changed_by_name?: string
    changed_at: string
}

// SLA history entry for a service
export interface SLAHistoryEntry {
    history_id: number
    service_sla_id: number
    service_id: number
    sla_template_id?: number
    template_name?: string
    uptime_guarantee?: number
    effective_from: string
    effective_to?: string
    action: 'created' | 'updated' | 'deleted'
    action_by?: number
    action_by_name?: string
    action_at: string
}
